import createFetchClient from "openapi-fetch";
import type { paths } from "../generated/schema";
import { SESSION_TOKEN_KEY } from "./use-session-token";
import {
  reportApiNetworkError,
  reportApiStatus,
  reportRequestStart,
  requestUrl,
} from "./api-availability";

function _readTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function _isTokenExpired(token: string): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" && payload.exp * 1000 < Date.now();
  } catch {
    return false;
  }
}

// Pluggable config — call before any API requests are made (e.g. at app init in baback-office).
// In customer-facing contexts (standalone widget) these defaults are used as-is.
let _getMerchantToken: () => string | null = () => null;
let _baseUrl = "https://api2.baback.co/";

export function configureMerchantToken(getter: () => string | null) {
  _getMerchantToken = getter;
}

export function configureReturnWidgetBaseUrl(url: string) {
  _baseUrl = url;
  _client = null; // invalidate so next call rebuilds with new URL
}

let _client: ReturnType<typeof createFetchClient<paths>> | null = null;

function buildClient() {
  return createFetchClient<paths>({
    baseUrl: _baseUrl,
    // Endpoints /public/ uniquement, authentifiés par Bearer : pas de cookie,
    // sinon le navigateur exige Access-Control-Allow-Credentials que le CORS
    // public ne renvoie plus (BAB-3040).
    credentials: "omit",
    fetch: async (input, init = {}) => {
      // openapi-fetch 0.17+ passes a fully-built Request object as `input` with all
      // headers (Content-Type, Authorization, etc.) already set. `init` is undefined
      // in browser contexts. Reading from `init.headers` gives an empty Headers object
      // and overrides the request headers — stripping Content-Type and breaking body parsing.
      const sourceHeaders = input instanceof Request ? input.headers : (init as RequestInit).headers;
      const headers = new Headers(sourceHeaders);

      // Inject auth token when the caller didn't set one explicitly.
      // Priority: merchant token (admin preview) → session token (customer JWT).
      // The Headers API trims values, so an "authorization: Bearer " placeholder
      // reads back as "Bearer" — comparing against the untrimmed string never
      // matched, and this fallback never ran.
      const existing = headers.get("authorization") ?? "";
      if (!existing || existing.trim() === "Bearer") {
        const merchantToken = _getMerchantToken();
        const rawSession = localStorage.getItem(SESSION_TOKEN_KEY) ?? "";
        const sessionToken = _isTokenExpired(rawSession) ? "" : rawSession;
        if (_isTokenExpired(rawSession) && rawSession) {
          localStorage.removeItem(SESSION_TOKEN_KEY);
        }
        const token = merchantToken || sessionToken || null;
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }
      }

      const url = requestUrl(input);
      const settled = reportRequestStart(url);

      let response: Response;
      try {
        response = await fetch(input as RequestInfo, {
          ...(init as RequestInit),
          headers,
        });
      } catch (error) {
        reportApiNetworkError(url);
        throw error;
      } finally {
        settled();
      }
      reportApiStatus(url, response.status);

      // Store the order session JWT for subsequent calls.
      const data = await response.clone().json().catch(() => null);
      const token = data?.token ?? data?.data?.token;
      if (token) {
        localStorage.setItem(SESSION_TOKEN_KEY, token.slice(0, -12));
      }

      return response;
    },
  });
}

export function getReturnWidgetFetchClient() {
  if (!_client) _client = buildClient();
  return _client;
}

export function getReturnWidgetBaseUrl() {
  return _baseUrl;
}

/**
 * Seeds the customer session with the signed order token carried by a return
 * link (`/a/return?...&orderId=X&bbt=<token>`).
 *
 * That token is the customer's proof of entitlement to the order: without it,
 * `load-with-items` has nothing but the order id to go on, and an order id is
 * not a secret (BAB-3042). Stored under the same key as the token minted by a
 * manual lookup, so every later call carries it and the next response replaces
 * it with a fresh one.
 *
 * Ignored rather than stored when empty, expired, or minted for another order:
 * it would then shadow a session token that is valid for the order actually
 * being opened. Pass `expectedOrderId` to have that checked.
 */
export function seedOrderLinkToken(
  token: string | null | undefined,
  expectedOrderId?: string | null,
) {
  if (!token || _isTokenExpired(token)) return;

  const tokenOrderId = _readTokenPayload(token)?.orderId;
  if (
    expectedOrderId &&
    typeof tokenOrderId === "string" &&
    tokenOrderId !== expectedOrderId
  ) {
    return;
  }

  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    // Private browsing with storage denied: the lookup falls back to the
    // manual identification form.
  }
}

/**
 * Returns the Authorization header for public widget endpoints.
 * Uses only the customer session token — merchant token is intentionally excluded
 * because endpoints like /public/return-request/search reject it with 401.
 * When the session token is absent the header is "Bearer " (empty), which causes
 * the fetch middleware to fall back to the merchant token for the initial request
 * (e.g. /public/orders/load-with-items before any session is established).
 */
export function getPublicAuthHeader() {
  const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY) ?? "";
  return { authorization: `Bearer ${sessionToken}` };
}

/**
 * Authorization header for the order identification call, the one endpoint where the
 * caller may still be an office user rather than a shopper. The merchant token is the
 * only credential OptionalAuthGuard can resolve to a user now that /public/* no longer
 * carries cookies, so it must win over any order-scoped token a previous lookup left in
 * storage — that token verifies as an order JWT, never as an access token, and the guard
 * would fall through to nobody. The token is set here rather than left to the fetch
 * middleware: this header is explicit, so the middleware treats it as caller-provided.
 * Every other public endpoint keeps the order token: they check it against the order
 * they act on. Customer contexts have no merchant token and are unaffected.
 */
export function getOrderLookupAuthHeader() {
  const merchantToken = _getMerchantToken();
  return merchantToken
    ? { authorization: `Bearer ${merchantToken}` }
    : getPublicAuthHeader();
}
